import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Save, 
  Send, 
  Upload,
  File,
  User,
  Mail,
  Phone,
  FileText,
  Truck,
  Wrench,
  Award,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  X,
  Loader2,
  Edit2,
  MapPin,
  Camera,
  Download,
  Eye,
  Building2
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AnswerStatus({ label, value, goodValue, BadIcon }) {
  let Icon = AlertCircle;
  let tone = 'text-slate-500';
  if (value === goodValue) {
    Icon = CheckCircle;
    tone = 'text-emerald-400';
  } else if (value) {
    Icon = BadIcon;
    tone = 'text-red-400';
  }
  return (
    <div className="flex items-center gap-2">
      <Icon className={tone} size={18} />
      <span className={tone}>{label}: {value ? value.toUpperCase() : 'Not answered'}</span>
    </div>
  );
}

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [application, setApplication] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [messageField, setMessageField] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [bankInfo, setBankInfo] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [appRes, msgRes] = await Promise.all([
        axios.get(`${API_URL}/admin/applications/${id}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/applications/${id}/messages`, { headers: getAuthHeaders() })
      ]);
      setApplication(appRes.data);
      setEditedData(appRes.data.data || {});
      setFieldErrors(appRes.data.field_errors || {});
      setMessages(msgRes.data || []);
      
      // Fetch bank info for this user
      if (appRes.data?.user_id) {
        try {
          const bankRes = await axios.get(`${API_URL}/admin/users/${appRes.data.user_id}/bank-info`, { headers: getAuthHeaders() });
          setBankInfo(bankRes.data);
        } catch { toast.error('Could not load bank info for this applicant'); }
      }
    } catch (error) {
      toast.error('Failed to load application');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(
        `${API_URL}/admin/applications/${id}/status?status=${newStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      setApplication(prev => ({ ...prev, status: newStatus }));
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleFieldErrorChange = (field, message) => {
    setFieldErrors(prev => ({
      ...prev,
      [field]: message
    }));
  };

  const saveFieldErrors = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/applications/${id}/field-errors`,
        { field_errors: fieldErrors },
        { headers: getAuthHeaders() }
      );
      setApplication(prev => ({ ...prev, field_errors: fieldErrors }));
      toast.success('Field corrections saved');
    } catch (error) {
      toast.error('Failed to save corrections');
    } finally {
      setSaving(false);
    }
  };

  const saveEditedData = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/applications/${id}`,
        { data: editedData },
        { headers: getAuthHeaders() }
      );
      setApplication(prev => ({ ...prev, data: editedData }));
      setEditMode(false);
      toast.success('Application data updated');
    } catch (error) {
      toast.error('Failed to update application');
    } finally {
      setSaving(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/applications/${id}/messages`,
        { 
          content: newMessage,
          field_reference: messageField || null
        },
        { headers: getAuthHeaders() }
      );
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      setMessageField('');
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', 'Admin Upload');

    try {
      const response = await axios.post(
        `${API_URL}/admin/applications/${id}/documents`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setApplication(prev => ({
        ...prev,
        documents: [...(prev.documents || []), response.data]
      }));
      toast.success('Document uploaded');
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-700 text-slate-300',
      submitted: 'bg-violet-500/20 text-violet-300',
      incomplete: 'bg-amber-500/20 text-amber-300',
      complete: 'bg-emerald-500/20 text-emerald-300'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${styles[status] || styles.draft}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  const data = editMode ? editedData : application?.data || {};

  return (
    <div className="own-type-scale min-h-screen bg-[#0A0A0F]">
      {/* Header */}
      <header className="bg-[#12121A] border-b border-slate-800 sm:sticky sm:top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="text-slate-400 hover:text-white hover:bg-slate-800 px-2 sm:px-4 flex-shrink-0"
                data-testid="back-to-admin"
              >
                <ArrowLeft size={18} className="sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-white truncate" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Application Details
                </h1>
                <p className="text-xs sm:text-sm text-slate-500 font-mono">{id?.slice(0, 8)}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {getStatusBadge(application?.status)}
              <Select value={application?.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="flex-1 sm:flex-none sm:w-40 bg-[#0A0A0F] border-slate-700 text-white" data-testid="status-select">
                  <SelectValue placeholder="Change Status" />
                </SelectTrigger>
                <SelectContent className="bg-[#12121A] border-slate-700">
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="incomplete">Incomplete</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-28 sm:pb-8">
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6 min-w-0">
            {/* Applicant Info Card */}
            <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6">
              <div className="flex items-center justify-between gap-3 mb-6">
                <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Applicant Information
                </h2>
                <Button
                  variant={editMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditMode(!editMode)}
                  className={`flex-shrink-0 ${editMode ? "bg-violet-600 hover:bg-violet-700" : "border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"}`}
                  data-testid="toggle-edit-mode"
                >
                  <Edit2 size={16} className="mr-2" />
                  {editMode ? 'Cancel Edit' : 'Edit'}
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Personal Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                    <User size={16} />
                    Personal Details
                  </h3>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-slate-500 text-xs">First Name</Label>
                      {editMode ? (
                        <Input
                          value={editedData.first_name || ''}
                          onChange={(e) => setEditedData(prev => ({ ...prev, first_name: e.target.value }))}
                          className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                        />
                      ) : (
                        <p className="font-medium text-white">{data.first_name || '-'}</p>
                      )}
                      {fieldErrors.first_name && (
                        <p className="text-red-400 text-xs mt-1">{fieldErrors.first_name}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-slate-500 text-xs">Last Name</Label>
                      {editMode ? (
                        <Input
                          value={editedData.last_name || ''}
                          onChange={(e) => setEditedData(prev => ({ ...prev, last_name: e.target.value }))}
                          className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                        />
                      ) : (
                        <p className="font-medium text-white">{data.last_name || '-'}</p>
                      )}
                      {fieldErrors.last_name && (
                        <p className="text-red-400 text-xs mt-1">{fieldErrors.last_name}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-slate-500 text-xs">Email</Label>
                      {editMode ? (
                        <Input
                          value={editedData.email || ''}
                          onChange={(e) => setEditedData(prev => ({ ...prev, email: e.target.value }))}
                          className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                        />
                      ) : (
                        <p className="font-medium text-white">{data.email || '-'}</p>
                      )}
                      {fieldErrors.email && (
                        <p className="text-red-400 text-xs mt-1">{fieldErrors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-slate-500 text-xs">Phone</Label>
                      {editMode ? (
                        <Input
                          value={editedData.phone || ''}
                          onChange={(e) => setEditedData(prev => ({ ...prev, phone: e.target.value }))}
                          className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                        />
                      ) : (
                        <p className="font-medium text-white">{data.phone || '-'}</p>
                      )}
                      {fieldErrors.phone && (
                        <p className="text-red-400 text-xs mt-1">{fieldErrors.phone}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Identification */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                    <FileText size={16} />
                    Identification
                  </h3>
                  
                  <div className="space-y-3">
                    <AnswerStatus label="U.S. Citizen" value={data.is_us_citizen} goodValue="yes" BadIcon={X} />
                    <AnswerStatus label="Criminal Record" value={data.has_criminal_record} goodValue="no" BadIcon={AlertCircle} />

                    <div>
                      <Label className="text-slate-500 text-xs">Social Security</Label>
                      {editMode ? (
                        <Input
                          value={editedData.social_security || ''}
                          onChange={(e) => setEditedData(prev => ({ ...prev, social_security: e.target.value }))}
                          className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                        />
                      ) : (
                        <p className="font-medium text-white font-mono">{data.social_security || '-'}</p>
                      )}
                      {fieldErrors.social_security && (
                        <p className="text-red-400 text-xs mt-1">{fieldErrors.social_security}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-slate-500 text-xs">Driver's License</Label>
                      {editMode ? (
                        <Input
                          value={editedData.driver_license || ''}
                          onChange={(e) => setEditedData(prev => ({ ...prev, driver_license: e.target.value }))}
                          className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                        />
                      ) : (
                        <p className="font-medium text-white font-mono">{data.driver_license || '-'}</p>
                      )}
                      {fieldErrors.driver_license && (
                        <p className="text-red-400 text-xs mt-1">{fieldErrors.driver_license}</p>
                      )}
                    </div>

                    {/* Photo Previews for ID docs */}
                    {application?.documents?.filter(d => ['Driver License', 'Social Security'].includes(d.document_type)).length > 0 && (
                      <div className="pt-2">
                        <Label className="text-slate-500 text-xs mb-2 block">Uploaded ID Photos</Label>
                        <div className="grid grid-cols-2 gap-3">
                          {application.documents.filter(d => ['Driver License', 'Social Security'].includes(d.document_type)).map(doc => (
                            <div key={doc.id} className="bg-[#0A0A0F] rounded-lg border border-slate-700 p-2 text-center">
                              <div className="flex items-center gap-2 mb-1">
                                <Camera size={12} className="text-violet-400" />
                                <span className="text-xs text-slate-400">{doc.document_type}</span>
                              </div>
                              {doc.filename.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                <img 
                                  src={`${API_URL}/applications/${application.id}/documents/${doc.id}`}
                                  alt={doc.document_type}
                                  className="w-full h-24 object-cover rounded border border-slate-700"
                                />
                              ) : (
                                <div className="w-full h-24 bg-slate-800 rounded flex items-center justify-center">
                                  <FileText className="text-slate-600" size={24} />
                                </div>
                              )}
                              <a
                                href={`${API_URL}/applications/${application.id}/documents/${doc.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-cyan-400 hover:text-cyan-300 mt-1 inline-flex items-center gap-1"
                              >
                                <Eye size={10} /> View
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Equipment */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                    <Truck size={16} />
                    Equipment & Resources
                  </h3>
                  
                  <div className="space-y-3">
                    {/* Selected Market */}
                    {data.selected_market && (
                      <div className="flex items-center gap-2">
                        <MapPin className="text-violet-400" size={18} />
                        <span className="text-slate-400 text-sm">Market:</span>
                        <span className="text-white font-medium">{data.selected_market}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      {data.has_truck ? (
                        <CheckCircle className="text-emerald-400" size={18} />
                      ) : (
                        <X className="text-slate-500" size={18} />
                      )}
                      <span className={data.has_truck ? 'text-emerald-400' : 'text-slate-500'}>
                        Has Own Truck
                      </span>
                    </div>
                    {data.truck_details && (
                      <p className="text-sm text-slate-400 ml-6">{data.truck_details}</p>
                    )}

                    {/* Needs Rent Vehicle */}
                    <div className="flex items-center gap-2">
                      {data.needs_rent_vehicle ? (
                        <AlertCircle className="text-amber-400" size={18} />
                      ) : (
                        <X className="text-slate-500" size={18} />
                      )}
                      <span className={data.needs_rent_vehicle ? 'text-amber-400' : 'text-slate-500'}>
                        Needs to Rent Vehicle
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {data.has_tools ? (
                        <CheckCircle className="text-emerald-400" size={18} />
                      ) : (
                        <X className="text-slate-500" size={18} />
                      )}
                      <span className={data.has_tools ? 'text-emerald-400' : 'text-slate-500'}>
                        Has Own Tools
                      </span>
                    </div>
                    {data.tools_details && (
                      <p className="text-sm text-slate-400 ml-6">{data.tools_details}</p>
                    )}
                    
                    <div className="flex items-center gap-2">
                      {data.has_meter ? (
                        <CheckCircle className="text-emerald-400" size={18} />
                      ) : (
                        <X className="text-slate-500" size={18} />
                      )}
                      <span className={data.has_meter ? 'text-emerald-400' : 'text-slate-500'}>
                        Has Meter Equipment
                      </span>
                    </div>
                    {data.meter_details && (
                      <p className="text-sm text-slate-400 ml-6">{data.meter_details}</p>
                    )}

                    {/* Needs Rent Meter */}
                    <div className="flex items-center gap-2">
                      {data.needs_rent_meter ? (
                        <AlertCircle className="text-amber-400" size={18} />
                      ) : (
                        <X className="text-slate-500" size={18} />
                      )}
                      <span className={data.needs_rent_meter ? 'text-amber-400' : 'text-slate-500'}>
                        Needs to Rent Meter
                      </span>
                    </div>

                    {/* Vehicle & Tools Photo Previews */}
                    {application?.documents?.filter(d => ['Vehicle Photo', 'Tools Photo'].includes(d.document_type)).length > 0 && (
                      <div className="pt-2">
                        <Label className="text-slate-500 text-xs mb-2 block">Equipment Photos</Label>
                        <div className="grid grid-cols-2 gap-3">
                          {application.documents.filter(d => ['Vehicle Photo', 'Tools Photo'].includes(d.document_type)).map(doc => (
                            <div key={doc.id} className="bg-[#0A0A0F] rounded-lg border border-slate-700 p-2 text-center">
                              <div className="flex items-center gap-2 mb-1">
                                <Camera size={12} className="text-violet-400" />
                                <span className="text-xs text-slate-400">{doc.document_type}</span>
                              </div>
                              {doc.filename.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                <img 
                                  src={`${API_URL}/applications/${application.id}/documents/${doc.id}`}
                                  alt={doc.document_type}
                                  className="w-full h-24 object-cover rounded border border-slate-700"
                                />
                              ) : (
                                <div className="w-full h-24 bg-slate-800 rounded flex items-center justify-center">
                                  <FileText className="text-slate-600" size={24} />
                                </div>
                              )}
                              <a
                                href={`${API_URL}/applications/${application.id}/documents/${doc.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-cyan-400 hover:text-cyan-300 mt-1 inline-flex items-center gap-1"
                              >
                                <Eye size={10} /> View
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Experience */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                    <Award size={16} />
                    Experience
                  </h3>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-slate-500 text-xs">Work Experience</Label>
                      {editMode ? (
                        <Textarea
                          value={editedData.work_experience || ''}
                          onChange={(e) => setEditedData(prev => ({ ...prev, work_experience: e.target.value }))}
                          className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                          rows={4}
                        />
                      ) : (
                        <p className="text-slate-300 text-sm whitespace-pre-wrap">{data.work_experience || '-'}</p>
                      )}
                      {fieldErrors.work_experience && (
                        <p className="text-red-400 text-xs mt-1">{fieldErrors.work_experience}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-slate-500 text-xs">Certifications</Label>
                      {editMode ? (
                        <Textarea
                          value={editedData.certifications || ''}
                          onChange={(e) => setEditedData(prev => ({ ...prev, certifications: e.target.value }))}
                          className="mt-1 bg-[#0A0A0F] border-slate-700 text-white"
                          rows={3}
                        />
                      ) : (
                        <p className="text-slate-300 text-sm whitespace-pre-wrap">{data.certifications || '-'}</p>
                      )}
                      {fieldErrors.certifications && (
                        <p className="text-red-400 text-xs mt-1">{fieldErrors.certifications}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bank Information */}
                {bankInfo && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                      <Building2 size={16} />
                      Bank Information
                    </h3>
                    <div className="space-y-3 bg-[#0A0A0F] rounded-lg p-4 border border-slate-700">
                      <div>
                        <Label className="text-slate-500 text-xs">Bank Name</Label>
                        <p className="font-medium text-white">{bankInfo.bank_name}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Routing Number</Label>
                        <p className="font-medium text-white font-mono">{bankInfo.routing_number}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Account Number</Label>
                        <p className="font-medium text-white font-mono">{bankInfo.account_number}</p>
                      </div>
                      <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
                        <CheckCircle className="text-emerald-400" size={14} />
                        <span className="text-xs text-emerald-400">Agreed to payment terms</span>
                        <span className="text-xs text-slate-600 ml-auto">{new Date(bankInfo.submitted_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {editMode && (
                <div className="mt-6 pt-6 border-t border-slate-700 flex sm:justify-end">
                  <Button
                    onClick={saveEditedData}
                    disabled={saving}
                    className="w-full sm:w-auto btn-gradient text-white"
                    data-testid="save-edits-btn"
                  >
                    {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save size={18} className="mr-2" />}
                    Save Changes
                  </Button>
                </div>
              )}
            </div>

            {/* Field Error Management */}
            <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6">
              <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                Mark Fields for Correction
              </h2>
              <p className="text-slate-400 text-sm mb-6">
                Add error messages to fields that need correction. These will appear as red messages on the applicant's dashboard.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {['first_name', 'last_name', 'email', 'phone', 'social_security', 'driver_license', 'work_experience', 'certifications'].map(field => (
                  <div key={field} className="space-y-2">
                    <Label className="text-sm text-slate-300 capitalize">{field.replace(/_/g, ' ')}</Label>
                    <Input
                      placeholder="Enter error message or leave blank"
                      value={fieldErrors[field] || ''}
                      onChange={(e) => handleFieldErrorChange(field, e.target.value)}
                      className={`bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-600 ${fieldErrors[field] ? 'border-red-500/50 bg-red-500/10' : ''}`}
                      data-testid={`field-error-${field}`}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-6 flex sm:justify-end">
                <Button
                  onClick={saveFieldErrors}
                  disabled={saving}
                  className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="save-field-errors-btn"
                >
                  {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <AlertCircle size={18} className="mr-2" />}
                  Save Corrections
                </Button>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Documents
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('admin-file-upload')?.click()}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                  data-testid="admin-upload-btn"
                >
                  <Upload size={16} className="mr-2" />
                  Upload
                </Button>
                <input
                  id="admin-file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>

              {application?.documents?.length > 0 ? (
                <div className="space-y-3">
                  {application.documents.map(doc => (
                    <div key={doc.id} className="flex items-center gap-3 p-3 sm:p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                      <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <File className="text-violet-400" size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{doc.filename}</p>
                        <p className="text-sm text-slate-500 truncate">{doc.document_type}</p>
                      </div>
                      <span className={`flex-shrink-0 text-xs px-2 py-1 rounded ${doc.uploaded_by === 'admin' ? 'bg-violet-500/20 text-violet-300' : 'bg-slate-700 text-slate-400'}`}>
                        {doc.uploaded_by === 'admin' ? 'Admin' : 'User'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No documents uploaded</p>
              )}
            </div>
          </div>

          {/* Sidebar - Messages */}
          <div className="space-y-4 sm:space-y-6 min-w-0">
            <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6">
              <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                <MessageSquare className="inline mr-2" size={20} />
                Messages
              </h2>

              {/* Message List */}
              <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
                {messages.length > 0 ? (
                  messages.map(msg => (
                    <div 
                      key={msg.id}
                      className={`p-3 rounded-lg ${msg.sender_role === 'admin' ? 'bg-violet-500/10 border-l-4 border-violet-500' : 'bg-[#0A0A0F]'}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-x-2 mb-1">
                        <span className="text-xs font-semibold text-slate-300">
                          {msg.sender_role === 'admin' ? 'You' : msg.sender_name}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(msg.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 break-words">{msg.content}</p>
                      {msg.field_reference && (
                        <p className="text-xs text-violet-400 mt-1">Re: {msg.field_reference}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-4 text-sm">No messages yet</p>
                )}
              </div>

              {/* Send Message */}
              <div className="border-t border-slate-700 pt-4 space-y-3">
                <Select value={messageField || "none"} onValueChange={(val) => setMessageField(val === "none" ? "" : val)}>
                  <SelectTrigger className="bg-[#0A0A0F] border-slate-700 text-white" data-testid="message-field-select">
                    <SelectValue placeholder="Reference a field (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12121A] border-slate-700">
                    <SelectItem value="none">No field reference</SelectItem>
                    <SelectItem value="first_name">First Name</SelectItem>
                    <SelectItem value="last_name">Last Name</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="social_security">Social Security</SelectItem>
                    <SelectItem value="driver_license">Driver's License</SelectItem>
                    <SelectItem value="work_experience">Work Experience</SelectItem>
                    <SelectItem value="certifications">Certifications</SelectItem>
                  </SelectContent>
                </Select>
                
                <Textarea
                  placeholder="Type a message to the applicant..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={3}
                  className="bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-600"
                  data-testid="message-input"
                />
                
                <Button
                  onClick={sendMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="w-full btn-gradient text-white"
                  data-testid="send-message-btn"
                >
                  {sendingMessage ? (
                    <Loader2 className="animate-spin mr-2" size={18} />
                  ) : (
                    <Send size={18} className="mr-2" />
                  )}
                  Send Message
                </Button>
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
                Application Info
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Created</span>
                  <span className="text-white">{new Date(application?.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Last Updated</span>
                  <span className="text-white">{new Date(application?.updated_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Documents</span>
                  <span className="text-white">{application?.documents?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Messages</span>
                  <span className="text-white">{messages.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
